import {
  Button,
  Card,
  Checkbox,
  Divider,
  InputNumber,
  Space,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import * as THREE from 'three';
import {
  createTestObjects,
  testOutOfBounds,
  testOverlap,
  testSnapping,
} from '../../utils/testSnapping';
import { SnappingDebugPanelProps } from './index';

const { Title, Text } = Typography;

const SnappingDebugPanel: React.FC<SnappingDebugPanelProps> = ({
  snappingService,
  scene,
  floorPlane,
  isSnappingEnabled,
  setIsSnappingEnabled,
  isGridSnappingEnabled,
  setIsGridSnappingEnabled,
  isEdgeSnappingEnabled,
  setIsEdgeSnappingEnabled,
  gridSize,
  setGridSize,
  snapThreshold,
  setSnapThreshold,
  isRotationSnappingEnabled,
  setIsRotationSnappingEnabled,
  rotationSnapAngle,
  setRotationSnapAngle,
  isUseCollisionGroups,
  setIsUseCollisionGroups,
}) => {
  const [testObjects, setTestObjects] = useState<{
    redCube?: THREE.Mesh;
    blueCube?: THREE.Mesh;
    greenSphere?: THREE.Mesh;
  }>({});

  // Create test objects when component mounts
  useEffect(() => {
    if (scene.current) {
      const objects = createTestObjects(scene.current);
      setTestObjects(objects);
    }

    return () => {
      // Clean up test objects when component unmounts
      if (scene.current) {
        Object.values(testObjects).forEach((obj) => {
          if (obj && scene.current) {
            scene.current.remove(obj);
          }
        });
      }
    };
  }, [scene]);

  // Update snapping service settings when state changes
  useEffect(() => {
    snappingService.setSnappingEnabled(isSnappingEnabled);
    snappingService.setGridSnappingEnabled(isGridSnappingEnabled);
    snappingService.setEdgeSnappingEnabled(isEdgeSnappingEnabled);
    snappingService.setRotationSnappingEnabled(isRotationSnappingEnabled);
    snappingService.setGridSize(gridSize);
    snappingService.setSnapThreshold(snapThreshold);
    snappingService.setRotationSnapAngle((rotationSnapAngle * Math.PI) / 180); // Convert degrees to radians

    // Update collision groups
    snappingService.setUseCollisionGroups(isUseCollisionGroups);
  }, [
    isSnappingEnabled,
    isGridSnappingEnabled,
    isEdgeSnappingEnabled,
    isRotationSnappingEnabled,
    gridSize,
    snapThreshold,
    rotationSnapAngle,
    isUseCollisionGroups,
  ]);

  const handleRunSnappingTest = () => {
    if (!scene.current || !testObjects.redCube) return;
    testSnapping(scene.current, testObjects.redCube);
  };

  const handleRunOverlapTest = () => {
    if (!scene.current || !testObjects.redCube) return;
    testOverlap(scene.current, testObjects.redCube);
  };

  const handleRunOutOfBoundsTest = () => {
    if (!scene.current || !floorPlane?.current || !testObjects.redCube) return;
    testOutOfBounds(scene.current, testObjects.redCube, floorPlane.current);
  };

  const handleShowSnapPoints = () => {
    if (!scene.current || !testObjects.redCube || !testObjects.blueCube) return;
    snappingService.visualizeSnapPoints(scene.current, testObjects.redCube);
    snappingService.visualizeSnapPoints(scene.current, testObjects.blueCube);
  };

  return (
    <Card
      title="Snapping Debug Panel"
      style={{
        position: 'absolute',
        top: '80px',
        right: '20px',
        width: '300px',
        zIndex: 1000,
        opacity: 0.9,
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Title level={5}>Snapping Configuration</Title>

        <Checkbox
          checked={isSnappingEnabled}
          onChange={(e) => setIsSnappingEnabled(e.target.checked)}
        >
          Enable Snapping
        </Checkbox>

        <Checkbox
          checked={isGridSnappingEnabled}
          onChange={(e) => setIsGridSnappingEnabled(e.target.checked)}
          disabled={!isSnappingEnabled}
        >
          Grid Snapping
        </Checkbox>

        <Space>
          <Text>Grid Size:</Text>
          <InputNumber
            value={gridSize}
            onChange={(value) => setGridSize(value || 0.25)}
            step={0.05}
            min={0.05}
            max={1}
            disabled={!isSnappingEnabled || !isGridSnappingEnabled}
          />
        </Space>

        <Checkbox
          checked={isEdgeSnappingEnabled}
          onChange={(e) => setIsEdgeSnappingEnabled(e.target.checked)}
          disabled={!isSnappingEnabled}
        >
          Edge Snapping
        </Checkbox>

        <Space>
          <Text>Snap Threshold:</Text>
          <InputNumber
            value={snapThreshold}
            onChange={(value) => setSnapThreshold(value || 0.2)}
            step={0.05}
            min={0.05}
            max={1}
            disabled={!isSnappingEnabled || !isEdgeSnappingEnabled}
          />
        </Space>

        <Checkbox
          checked={isRotationSnappingEnabled}
          onChange={(e) => setIsRotationSnappingEnabled(e.target.checked)}
          disabled={!isSnappingEnabled}
        >
          Rotation Snapping
        </Checkbox>

        <Space>
          <Text>Rotation Angle (°):</Text>
          <InputNumber
            value={rotationSnapAngle}
            onChange={(value) => setRotationSnapAngle(value || 45)}
            step={5}
            min={5}
            max={90}
            disabled={!isSnappingEnabled || !isRotationSnappingEnabled}
          />
        </Space>

        <Divider />
        <Title level={5}>Collision Groups</Title>

        <Checkbox
          checked={isUseCollisionGroups}
          onChange={(e) => setIsUseCollisionGroups(e.target.checked)}
        >
          Use Collision Groups
        </Checkbox>

        <Title level={5} style={{ marginTop: '16px' }}>
          Test Functions
        </Title>

        <Space direction="vertical" style={{ width: '100%' }}>
          <Button type="primary" onClick={handleRunSnappingTest} block>
            Test Snapping
          </Button>

          <Button onClick={handleRunOverlapTest} block>
            Test Overlap Detection
          </Button>

          <Button onClick={handleRunOutOfBoundsTest} block>
            Test Out of Bounds
          </Button>

          <Button onClick={handleShowSnapPoints} block>
            Show Snap Points
          </Button>
        </Space>

        <Text type="secondary" style={{ fontSize: '12px', marginTop: '16px' }}>
          Check the console for test results. Visual feedback will appear in the
          scene.
        </Text>
      </Space>
    </Card>
  );
};

export default SnappingDebugPanel;
